/**
 * RevenueCat — native In-App Purchase (Apple 3.1.1 / Play Billing).
 *
 * Native apps must sell subscriptions through StoreKit / Play Billing; we wrap
 * that with RevenueCat. This module is the single entry point the UI uses. It
 * is INERT on web and when no key is configured, so the web build and the
 * web-only LemonSqueezy flow are untouched — the native shell uses RevenueCat,
 * the web uses LemonSqueezy.
 *
 * Env (build-time, set on the CF Pages build — §2):
 *   REACT_APP_REVENUECAT_IOS_KEY      appl_...   (RevenueCat public SDK key, iOS)
 *   REACT_APP_REVENUECAT_ANDROID_KEY  goog_...   (RevenueCat public SDK key, Android)
 *   REACT_APP_REVENUECAT_ENTITLEMENT  entitlement id that grants access (default "premium")
 *
 * The plugin is loaded via dynamic import ONLY on native so its web stub never
 * ends up in the web bundle path and can't throw during SSR/prerender.
 */
import { isNative, isIOS } from "@/utils/capacitor";

const IOS_KEY = process.env.REACT_APP_REVENUECAT_IOS_KEY || "";
const ANDROID_KEY = process.env.REACT_APP_REVENUECAT_ANDROID_KEY || "";
export const ENTITLEMENT_ID =
  process.env.REACT_APP_REVENUECAT_ENTITLEMENT || "premium";

const apiKey = () => (isIOS ? IOS_KEY : ANDROID_KEY);

/** True only when native IAP can actually run (native platform + a key set). */
export const isRevenueCatAvailable = () => isNative && !!apiKey();

let _configured = false;

/**
 * Configure the SDK with OUR user id as the RevenueCat appUserID, so the
 * webhook's `app_user_id` maps straight back to `db.users.id`. Idempotent.
 */
export const initRevenueCat = async (userId) => {
  if (!isRevenueCatAvailable() || _configured) return;
  try {
    const { Purchases, LOG_LEVEL } = await import(
      "@revenuecat/purchases-capacitor"
    );
    await Purchases.setLogLevel({ level: LOG_LEVEL.ERROR });
    await Purchases.configure({
      apiKey: apiKey(),
      appUserID: userId || undefined,
    });
    _configured = true;
  } catch (e) {
    // Never let a store hiccup break the app — the UI falls back to "manage
    // your plan on the web".
    // eslint-disable-next-line no-console
    console.warn("RevenueCat configure failed", e);
  }
};

/** Current offering's packages (already localized prices from the store). */
export const getPackages = async () => {
  if (!isRevenueCatAvailable()) return [];
  try {
    const { Purchases } = await import("@revenuecat/purchases-capacitor");
    const offerings = await Purchases.getOfferings();
    return offerings?.current?.availablePackages || [];
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("RevenueCat getOfferings failed", e);
    return [];
  }
};

/** Purchase a package. Returns { active: bool } from the fresh entitlements. */
export const purchasePackage = async (pkg) => {
  const { Purchases } = await import("@revenuecat/purchases-capacitor");
  const res = await Purchases.purchasePackage({ aPackage: pkg });
  const active = !!res?.customerInfo?.entitlements?.active?.[ENTITLEMENT_ID];
  return { active, customerInfo: res?.customerInfo };
};

/** Restore prior purchases (App Store requirement). Returns { active }. */
export const restorePurchases = async () => {
  if (!isRevenueCatAvailable()) return { active: false };
  const { Purchases } = await import("@revenuecat/purchases-capacitor");
  const info = await Purchases.restorePurchases();
  const active = !!info?.customerInfo?.entitlements?.active?.[ENTITLEMENT_ID];
  return { active, customerInfo: info?.customerInfo };
};

/** Whether the current customer already holds the entitlement. */
export const hasActiveEntitlement = async () => {
  if (!isRevenueCatAvailable()) return false;
  try {
    const { Purchases } = await import("@revenuecat/purchases-capacitor");
    const { customerInfo } = await Purchases.getCustomerInfo();
    return !!customerInfo?.entitlements?.active?.[ENTITLEMENT_ID];
  } catch {
    return false;
  }
};
